import React, { useMemo, useState } from "react";
import { AlertTriangle, BarChart3, ScatterChart as ScatterChartIcon, ShieldAlert } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  FinanceMarginDriverGrouping,
  FinanceMarginDriversAnalysis,
  ProjectMarginAnalyticsSummary,
} from "../models/production";
import { cn, formatCurrency } from "../utils";

const BAR_COLOUR = "#0f172a";
const SCATTER_COLOUR = "#0f766e";

const GROUP_OPTIONS: Array<{ id: FinanceMarginDriverGrouping; label: string }> = [
  { id: "size", label: "By Size" },
  { id: "duration", label: "By Duration" },
  { id: "rate", label: "By Rate" },
  { id: "department_mix", label: "By Department Mix" },
];

function formatPercent(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `${Math.round(value * 10) / 10}%`;
}

function metricTone(value?: number) {
  if (typeof value !== "number") return "text-slate-900 dark:text-slate-100";
  if (value < 0) return "text-rose-700 dark:text-rose-300";
  if (value < 10) return "text-amber-700 dark:text-amber-300";
  return "text-emerald-700 dark:text-emerald-300";
}

function GroupTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ payload: { bucketLabel: string; averageMarginPercent?: number; totalRevenue: number; projectCount: number; averageOverrunPercent?: number; marginVariance?: number } }>;
  currency: string;
}) {
  const bucket = payload?.[0]?.payload;
  if (!active || !bucket) return null;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-lg">
      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{bucket.bucketLabel}</div>
      <div className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300">
        <div>Average margin: {formatPercent(bucket.averageMarginPercent)}</div>
        <div>Total revenue: {formatCurrency(bucket.totalRevenue, currency)}</div>
        <div>Projects: {bucket.projectCount}</div>
        <div>Average overrun: {formatPercent(bucket.averageOverrunPercent)}</div>
        <div>Margin variance: {typeof bucket.marginVariance === "number" ? bucket.marginVariance : "—"}</div>
      </div>
    </div>
  );
}

function ScatterTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ payload: ProjectMarginAnalyticsSummary }>;
  currency: string;
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-lg">
      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{point.projectName}</div>
      <div className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300">
        <div>Duration: {point.projectDurationDays ?? "—"} days</div>
        <div>Margin: {formatPercent(point.analysisGrossMarginPercent)}</div>
        <div>Bid value: {formatCurrency(point.totalBidValue || 0, currency)}</div>
        <div>Effective rate: {typeof point.impliedRateCardAtBid === "number" ? formatCurrency(point.impliedRateCardAtBid, currency) : "—"}/day</div>
        <div>Basis: {point.analysisBasis === "final_actual" ? "Final actual" : "Live current"}</div>
      </div>
    </div>
  );
}

export function MarginDriversAnalysis({
  analysis,
  currency,
}: {
  analysis: FinanceMarginDriversAnalysis;
  currency: string;
}) {
  const [grouping, setGrouping] = useState<FinanceMarginDriverGrouping>("size");

  const bucketRows = useMemo(() => {
    switch (grouping) {
      case "duration":
        return analysis.averageMarginByDuration;
      case "rate":
        return analysis.averageMarginByRate;
      case "department_mix":
        return analysis.averageMarginByDepartmentMix;
      case "size":
      default:
        return analysis.averageMarginBySize;
    }
  }, [analysis, grouping]);

  const chartHeight = Math.max(280, bucketRows.length * 48);
  const lowestMarginProjects = useMemo(
    () =>
      [...analysis.projects]
        .filter((project) => typeof project.analysisGrossMarginPercent === "number")
        .sort((a, b) => (a.analysisGrossMarginPercent || 0) - (b.analysisGrossMarginPercent || 0))
        .slice(0, 8),
    [analysis.projects]
  );

  const totalRevenue = analysis.projects.reduce((sum, project) => sum + (project.totalBidValue || 0), 0);
  const averageMargin = useMemo(() => {
    const values = analysis.projects
      .map((project) => project.analysisGrossMarginPercent)
      .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
    if (!values.length) return undefined;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }, [analysis.projects]);

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
      <div className="border-b border-slate-200 dark:border-slate-800 px-5 py-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Margin Drivers</div>
            <div className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
              A company-level analytical layer that compares project margin against size, duration, rate pressure, and department mix without changing the core finance truth model.
            </div>
          </div>
          <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-1">
            {GROUP_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setGrouping(option.id)}
                className={cn(
                  "rounded-lg px-3 py-2 text-sm font-medium transition",
                  grouping === option.id ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-950 shadow-sm" : "text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-900"
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-5 px-5 py-5">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Projects Analysed</div>
            <div className="mt-3 text-2xl font-semibold text-slate-900 dark:text-slate-100">{analysis.projects.length}</div>
            <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">Active projects currently in finance scope.</div>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Total Revenue</div>
            <div className="mt-3 text-2xl font-semibold text-slate-900 dark:text-slate-100">{formatCurrency(totalRevenue, currency)}</div>
            <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">Combined bid value across analysed projects.</div>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Average Margin</div>
            <div className={cn("mt-3 text-2xl font-semibold", metricTone(averageMargin))}>{formatPercent(averageMargin)}</div>
            <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">Uses final actual where available, otherwise current live margin.</div>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Overrun Correlation</div>
            <div className={cn("mt-3 text-2xl font-semibold", metricTone(analysis.overrunMarginCorrelation && analysis.overrunMarginCorrelation < 0 ? -1 : 12))}>
              {typeof analysis.overrunMarginCorrelation === "number" ? analysis.overrunMarginCorrelation : "—"}
            </div>
            <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">Correlation between average overrun % and margin %. Negative values suggest overruns erode margin.</div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.7fr_1fr]">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
              <BarChart3 className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              Average Margin by {GROUP_OPTIONS.find((option) => option.id === grouping)?.label.replace("By ", "")}
            </div>
            {bucketRows.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                Margin analysis needs more project finance coverage before there is anything comparable to chart.
              </div>
            ) : (
              <div style={{ height: chartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bucketRows} margin={{ top: 12, right: 24, left: 12, bottom: 12 }}>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="bucketLabel" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(value) => formatPercent(Number(value))} />
                    <Tooltip content={<GroupTooltip currency={currency} />} cursor={{ fill: "rgba(148, 163, 184, 0.08)" }} />
                    <Bar dataKey="averageMarginPercent" radius={[6, 6, 0, 0]}>
                      {bucketRows.map((bucket) => (
                        <Cell key={bucket.bucketId} fill={BAR_COLOUR} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
              <ShieldAlert className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              Summary Insights
            </div>
            <div className="space-y-3">
              {analysis.insights.map((insight) => (
                <div
                  key={insight.id}
                  className={cn(
                    "rounded-xl border px-3 py-3 text-sm",
                    insight.tone === "warning"
                      ? "border-amber-200 bg-amber-50 text-amber-900"
                      : insight.tone === "positive"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                        : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  )}
                >
                  <div className="font-medium">{insight.summary}</div>
                  <div className="mt-1 text-xs leading-5 opacity-80">{insight.helperText}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
            <ScatterChartIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            Duration vs Margin
          </div>
          {analysis.projects.filter((project) => typeof project.projectDurationDays === "number" && typeof project.analysisGrossMarginPercent === "number").length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              This scatter view needs projects with both duration and margin data.
            </div>
          ) : (
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 12, right: 24, left: 12, bottom: 12 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    dataKey="projectDurationDays"
                    name="Duration"
                    unit="d"
                    tick={{ fill: "#64748b", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="number"
                    dataKey="analysisGrossMarginPercent"
                    name="Margin"
                    unit="%"
                    tick={{ fill: "#64748b", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<ScatterTooltip currency={currency} />} cursor={{ strokeDasharray: "3 3" }} />
                  <Scatter data={analysis.projects} fill={SCATTER_COLOUR} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="grid grid-cols-[minmax(0,1.7fr)_110px_110px_120px_110px_110px] gap-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            <div>Project</div>
            <div>Size</div>
            <div>Duration</div>
            <div>Rate Bucket</div>
            <div>Margin %</div>
            <div>Overrun %</div>
          </div>
          {lowestMarginProjects.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">No project analytics are ready yet.</div>
          ) : (
            lowestMarginProjects.map((project) => (
              <div key={project.projectId} className="grid grid-cols-[minmax(0,1.7fr)_110px_110px_120px_110px_110px] gap-3 border-b border-slate-100 dark:border-slate-800 px-4 py-3 text-sm last:border-b-0">
                <div className="min-w-0">
                  <div className="truncate font-medium text-slate-900 dark:text-slate-100">{project.projectName}</div>
                  <div className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                    {project.analysisBasis === "final_actual" ? "Final actual" : "Live current"} • {project.dominantDepartment || "Unmapped"}
                  </div>
                </div>
                <div className="text-slate-700 dark:text-slate-300">{project.sizeBucket}</div>
                <div className="text-slate-700 dark:text-slate-300">{project.projectDurationDays ? `${project.projectDurationDays}d` : "—"}</div>
                <div className="text-slate-700 dark:text-slate-300">{project.rateBucket}</div>
                <div className={cn("font-medium", metricTone(project.analysisGrossMarginPercent))}>{formatPercent(project.analysisGrossMarginPercent)}</div>
                <div className={cn("font-medium", metricTone((project.averageOverrunPercent || 0) * -1))}>{formatPercent(project.averageOverrunPercent)}</div>
              </div>
            ))
          )}
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              This first version uses the current project finance rollups as its source. It is designed to help us compare margin patterns safely now, and it leaves room for later extensions into predictive modelling, richer forecasting, pricing optimisation, and exportable management reports.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
